import { lazy, memo, Suspense, useCallback } from "react";
import { IGlobalNotification, InfoBarBackgroundThemeEnum } from "@/common/services/types";
import { clsx } from "clsx";
import CloseIcon from "@/common/images/icons/x.svg?react";
import "./InfoBar.scss";

const BlackFridayTheme = lazy(() => import("@/components/alert-theme-black-friday/BlackFridayTheme"));
const HalloweenTheme = lazy(() => import("@/components/alert-theme-halloween/HalloweenTheme"));
const ChristmasTheme = lazy(() => import("@/components/alert-theme-christmas/ChristmasTheme"));
const ValentineTheme = lazy(() => import("@/components/alert-theme-valentine/ValentineTheme"));
const StandardTheme = lazy(() => import("@/components/alert-theme-standard/StandardTheme"));

interface IInfoBar {
  onLinkAction: (id: string) => void;
  onCloseInfoBar: (id: string) => void;
  notification: IGlobalNotification;
  showNotify: boolean;
}

export const InfoBar: React.FC<IInfoBar> = memo(({
  onLinkAction,
  onCloseInfoBar,
  notification,
  showNotify
}) => {
  const { background_theme, text, text_theme, cta_text, cta_link, close_button_enabled, id } = notification;

  const onLinkClick = useCallback(async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    await onLinkAction(id);
    location.href = cta_link;
  }, [cta_link, id, onLinkAction]);

  const onClose = useCallback(() => {
    onCloseInfoBar(id);
  }, [id, onCloseInfoBar]);

  const renderDecor = useCallback(() => {
    switch (background_theme) {
    case InfoBarBackgroundThemeEnum.halloween:
      return <HalloweenTheme />;
    case InfoBarBackgroundThemeEnum.blackFriday:
      return <BlackFridayTheme />;
    case InfoBarBackgroundThemeEnum.christmas:
      return <ChristmasTheme />;
    case InfoBarBackgroundThemeEnum.valentine:
      return <ValentineTheme />;
    case InfoBarBackgroundThemeEnum.standard:
      return <StandardTheme />;
    default:
      return null;
    }
  }, [background_theme]);

  return (
    <div className={clsx(
      "InformationBar",
      !showNotify && "InformationBar_isHidden",
      `InformationBar_is${background_theme}`,
      `InformationBar_is${text_theme}`,
      close_button_enabled && "InformationBar_withCloseBtn"
    )}>
      <div className="InformationBar-Wrap">

        <div className="InformationBar-Info">
          <p className="InformationBar-Text">{text}</p>
          <button className="InformationBar-Link" rel="info-bar" onClick={onLinkClick}>{cta_text}</button>
        </div>

        {!!close_button_enabled &&
          <button className="InformationBar-CloseBtn" onClick={onClose} aria-label="Close Information Bar">
            <CloseIcon />
          </button>
        }

        <Suspense>
          {renderDecor()}
        </Suspense>
      </div>
    </div>
  );
});
